import * as React from 'react';
import { useContext, useRef, useState, useCallback } from 'react'; // Added useCallback
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useDropzone } from 'react-dropzone';
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid2";
import FormControl from "@mui/material/FormControl";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Dialog from "@mui/material/Dialog";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { v4 as uuidv4 } from 'uuid';
import { getTokenHeader, appConfig } from "../../../utils/settings";
import { AuthContext } from "../../../context/authContext";
import { getCategoryIdByDescription } from "./categoryMatcher";
import { DataGrid } from '@mui/x-data-grid';
import InputLabel from "@mui/material/InputLabel";

const PREFIX = 'TransactionsUpload';
const classes = {
   root: `${PREFIX}-root`,
   paper: `${PREFIX}-paper`,
   rightIcon: `${PREFIX}-rightIcon`,
}
const Root = styled('form')(({ theme }) => ({
   [`&.${classes.root}`]: {
      flexGrow: 1,
   },
   [`& .${classes.paper}`]: {
      padding: theme.spacing(2),
      color: theme.palette.text.secondary,
      width: '100%'
   },
   [`& .${classes.rightIcon}`]: {
      marginLeft: theme.spacing(1),
   },
}))

const DropzoneArea = styled(Box)(({ theme }) => ({
   width: '100%',
   height: '100px',
   border: '2px dotted',
   borderColor: theme.palette.primary.main,
   borderRadius: '5px',
   padding: '20px',
   textAlign: 'center',
   cursor: 'pointer',
   display: 'flex',
   alignItems: 'center',
   justifyContent: 'center',
}));

export default function TransactionsUpload(props) {
   const { open, handleClose, categories: accountCategories } = props;
   const { setPasswordExpired } = useContext(AuthContext);
   const [transactions, setTransactions] = useState([]);
   const inputRef = useRef(null);
   const [bank, setBank] = useState("wells_fargo");

   // Memoize categories for DataGrid valueOptions to prevent re-renders
   const categoryOptions = React.useMemo(() => {
      return accountCategories?.map(cat => ({
         value: cat.id,
         label: cat.category
      })) || []; // Ensure it's an array even if categories is null/undefined
   }, [accountCategories]);

   const { getRootProps, getInputProps, isDragActive } = useDropzone({
      accept: {
         'text/csv': ['.csv'],
      },
      onDrop: (acceptedFiles) => {
         if (acceptedFiles.length > 0) {
            handleCSVUpload(acceptedFiles[0]);
         }
      }
   });

   const handleBankChange = (event) => {
      setBank(event.target.value);
      setTransactions([]);
   }

   const handleRowCategoryChange = (transactionId, newCategoryId) => {
      setTransactions((prevTransactions) => {
         return prevTransactions.map((transaction) => {
            if (transaction.id === transactionId) {
               return { ...transaction, category: newCategoryId }; // Update category ID
            }
            console.log(transaction);
            return transaction; // Leave other transactions unchanged
         });
      });
   };

   const handleImport = () => {
      let reqHeaders = getTokenHeader();
      if (!reqHeaders) {
         console.error("Authentication token not found.");
         // Handle appropriately - maybe show an error message
         return;
      }
      reqHeaders = { ...reqHeaders, "Content-Type": "application/json" };

      console.log("Importing:", transactions);

      const chunkedTransactions = [];
      const chunkSize = 100; // Define chunk size
      for (let i = 0; i < transactions.length; i += chunkSize) {
         chunkedTransactions.push(transactions.slice(i, i + chunkSize));
      }

      // Use Promise.all to handle multiple fetch requests concurrently
      Promise.all(chunkedTransactions.map(chunk =>
         fetch(appConfig.host + '/api/v1/transactions/import', {
            method: 'POST',
            headers: reqHeaders,
            body: JSON.stringify(chunk), // Send the chunk directly
         }).then(response => {
            if (response.status === 401) {
               setPasswordExpired(true);
               throw new Error('Unauthorized'); // Throw error to stop processing further
            }
            if (!response.ok) {
               // Handle other errors (e.g., server errors)
               throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json(); // Or response.text() depending on API
         })
      )).then(() => {
         console.log('All chunks imported successfully.');
         handleClose(); // Close dialog only after all requests succeed
      }).catch(error => {
         console.error('Import request failure:', error);
         if (error.message !== 'Unauthorized') {
            // Show an error message to the user (e.g., using a toast/snackbar)
            alert(`Failed to import transactions: ${error.message}`);
         }
         // Do not automatically close the dialog on error unless it's handled
      });
   }

   const handleCSVUpload = async (file) => {
      if (!file) return;

      try {
         const reader = new FileReader();
         reader.onload = (e) => {
            const csvData = e.target.result;
            // Ensure categories are available before parsing
            if (!accountCategories || accountCategories.length === 0) {
               console.error("Categories not loaded yet.");
               alert("Categories are not available. Please try again."); // Inform user
               return;
            }
            const parsedTransactions = parseCSVData(csvData, accountCategories);
            setTransactions(parsedTransactions);
         };
         reader.readAsText(file);
      } catch (error) {
         console.error('Error uploading or parsing file:', error);
         alert(`Error processing CSV: ${error.message}`); // Inform user
      } finally {
         // Clear the input value to allow re-uploading the same file
         // Accessing inputRef.current might be tricky with react-dropzone's input
         // Resetting dropzone state might be needed if re-upload fails
      }
   };

   // Pass categories directly to parser
   const parseCSVData = (csvData, currentCategories) => {
      const lines = csvData.split('\n');
      let parsed = [];

      for (const line of lines) {
         if (line.trim() === '') continue; // Skip empty lines

         // More robust CSV splitting (handles quotes containing commas)
         let values = line.match(/(".*?"|[^",]+)(?=\s*,|\s*$)/g)?.map(val => val.replace(/^"|"$/g, '')) || [];
         // Fallback for simpler cases if regex fails or isn't needed
         if (!values || values.length < 5) {
            // Simpler split if regex fails (adjust index if needed)
            const simpleValues = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
            // Add basic validation
            if (simpleValues.length >= 5) {
               values = simpleValues; // Be careful with simple split
            } else {
               console.warn("Skipping malformed line:", line);
               continue; // Skip malformed line
            }
         }


         if (bank === 'wells_fargo') {
            // Ensure enough values are present for Wells Fargo format
            if (values.length >= 5) {
               const transaction = parseWellsFargoData(values, currentCategories);
               // Don't save credits (adjust logic as needed for returns/credits)
               if (transaction && transaction.amount > 0) {
                  parsed.push(transaction);
               }
            } else {
               console.warn("Skipping row with unexpected Wells Fargo format:", values);
            }
         } else {
            console.warn("Unsupported bank type selected for parsing:", bank);
            alert("Unsupported bank type selected."); // Inform user
            return []; // Stop parsing if bank is unsupported
         }
      }
      console.log(parsed);
      return parsed;
   };

   // Pass categories directly to parser
   function parseWellsFargoData(data, currentCategories) {
      try {
         // Assuming Wells Fargo format: Date, Amount, ?, ?, Description
         // Indexes might need adjustment based on actual CSV export
         const transactionDateString = data[0];
         const amountString = data[1];
         const description = data[4] || "Other"; // Use index 4 for description

         // Basic validation
         if (!transactionDateString || isNaN(parseFloat(amountString))) {
            console.warn("Skipping row with invalid date or amount:", data);
            return null;
         }

         const epochMs = new Date(transactionDateString).getTime();
         // Check if date parsing was successful
         if (isNaN(epochMs)) {
            console.warn("Skipping row with invalid date format:", transactionDateString);
            return null;
         }

         // Removed timezone offset calculation - backend should handle timezone conversions
         // Send UTC epoch seconds
         const transactionInstant = epochMs / 1000;
         const amount = parseFloat(amountString);

         // Ensure categories are available before calling matcher
         const categoryId = currentCategories && currentCategories.length > 0
            ? getCategoryIdByDescription(description, currentCategories)
            : null; // Handle case where categories might not be ready

         return {
            id: uuidv4(), // Crucial: DataGrid needs a unique id for each row
            transactionDate: transactionInstant,
            amount: amount * -1, // Invert amount for debits
            category: categoryId, // Store the matched category ID
            description: description,
         };
      } catch (error) {
         console.error("Error parsing Wells Fargo row:", data, error);
         return null; // Return null for rows that cause errors
      }
   }

   // --- DataGrid Column Definition ---
   const columns = React.useMemo(() => [
      {
         field: 'transactionDate',
         headerName: 'Date',
         width: 120,
         valueFormatter: (params) => { // Use valueFormatter for display
            if (params == null) return '';
            const date = new Date(params * 1000);
            const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
            return date.toLocaleDateString('en-US', options); // Use locale appropriate for users
         },
         type: 'date',
         sortable: true,
      },
      {
         field: 'amount',
         headerName: 'Amount',
         width: 120,
         type: 'number',
         valueFormatter: (params) => { // Format as currency
            if (params == null) return '';
            return `$${params.toFixed(2)}`;
         },
         align: 'right',
         headerAlign: 'right',
         sortable: true,
      },
      {
         field: 'description',
         headerName: 'Description',
         flex: 1, // Take remaining space
         minWidth: 250,
         sortable: true,
      },
      {
         field: 'category',
         headerName: 'Category',
         width: 200,
         renderCell: (params) => {
            const rowData = params.row;
            const selectId = `category-select-${rowData.id}`;
            return (
               <FormControl fullWidth size="medium" sx={{ my: "7px", mx: 0.5 }}>
                  <Select
                     fullWidth
                     id={selectId}
                     value={rowData.category} // Value from transaction data
                     onChange={(event) =>
                        handleRowCategoryChange(rowData.id, event.target.value)
                     }
                  >
                     {accountCategories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                           {category.category}
                        </MenuItem>
                     ))}
                  </Select>
               </FormControl>
               )
         },
         editable:false,
         sortable: true,
      },
   ], [categoryOptions]); // Depend on memoized categoryOptions

   // --- DataGrid Edit Handling ---
   const processRowUpdate = useCallback(
      (newRow, oldRow) => {
         // Find the transaction in state and update it
         setTransactions((prevTransactions) =>
            prevTransactions.map((transaction) =>
               transaction.id === newRow.id ? { ...transaction, ...newRow } : transaction
            )
         );
         // Return the newRow to update the DataGrid internal state
         return newRow;
      },
      [setTransactions] // Dependency
   );

   const handleProcessRowUpdateError = useCallback((error) => {
      // Handle errors during row updates if needed (e.g., validation errors)
      console.error("DataGrid row update error:", error);
      // Optionally show a message to the user
   }, []);


   return (
      <Root>
         <Dialog open={open} onClose={handleClose} maxWidth={'md'}>
            <DialogTitle>Upload Exported Transactions</DialogTitle>
            <DialogContent>
               <Grid container sx={{paddingTop: "5px"}} rowSpacing={2} columnSpacing={2} alignItems={"center"}>
                  <Grid size={{sm: 12}}>
                        <Select
                           labelId="bank-select-label"
                           id="bank-select"
                           value={bank}
                           onChange={handleBankChange}
                           fullWidth={true}
                        >
                           <MenuItem value={"wells_fargo"}>Wells Fargo</MenuItem>
                        </Select>
                  </Grid>
                  <Grid size={{sm: 12}}>
                     <DropzoneArea {...getRootProps()}>
                        <input {...getInputProps()} />
                        {
                           isDragActive ?
                              <Typography color="primary">Drop the CSV file here ...</Typography> :
                              <Typography color="primary">Drag 'n' drop a CSV file here, or click to select</Typography>
                        }
                     </DropzoneArea>
                  </Grid>

                  {/* DataGrid Display Area */}
                  <Grid size={{sm: 12}}>
                     {/* Set height for the DataGrid */}
                     <Box sx={{ height: 500, width: '100%', mt: 2 }}>
                        {/* Conditionally render DataGrid only when transactions exist */}
                        {console.log(transactions)}
                        {transactions.length > 0 ? (
                           <DataGrid
                              rowHeight={70}
                              rows={transactions}
                              columns={columns}
                              processRowUpdate={processRowUpdate}
                              onProcessRowUpdateError={handleProcessRowUpdateError}
                              disableRowSelectionOnClick
                              initialState={{
                                 sorting: {
                                    sortModel: [{ field: 'transactionDate', sort: 'desc' }],
                                 },
                              }}
                              pageSizeOptions={[50, 100]}
                           />
                        ) : (
                           <Typography sx={{ textAlign: 'center', mt: 4 }}>
                              {bank ? 'Export bank transactions as CSV and upload them here.' : 'Select a bank first.'}
                           </Typography>
                        )}
                     </Box>
                  </Grid>
               </Grid>
            </DialogContent>
            <DialogActions>
               <Button onClick={handleClose}>Cancel</Button> {/* Use handleClose directly */}
               <Button
                  color='secondary'
                  onClick={handleImport}
                  disabled={transactions.length === 0} // Disable import if no transactions
               >
                  Import ({transactions.length}) {/* Show count */}
               </Button>
            </DialogActions>
         </Dialog>
      </Root>
   );
}